import React, { createContext, useContext, useState, ReactNode } from "react";

export interface SpecificationAnalysis {
  question: string;
  result: string;
}

// Relax type to accept schema-driven dynamic keys from backend
export interface ReportData {
  [key: string]: any;
}

export interface AnalysisHistoryItem {
  stage: string;
  content: string;
  timestamp: string;
}

export interface ReportState {
  overview: string | null;
  specificationAnalysis: SpecificationAnalysis[];
  report: ReportData | null;
  threadId: string | null;
  analysisHistory: AnalysisHistoryItem[];
}

interface ReportContextType {
  reportState: ReportState;
  updateOverview: (overview: string) => void;
  updateSpecificationAnalysis: (analysis: SpecificationAnalysis[]) => void;
  updateReport: (report: ReportData) => void;
  updateThreadId: (threadId: string) => void;
  updateAnalysisHistory: (history: AnalysisHistoryItem[]) => void;
  clearAll: () => void;
  isResetting: boolean;
  setResetting: (resetting: boolean) => void;
  isAutoTriggeringFromUrl: boolean;
  setIsAutoTriggeringFromUrl: (isAutoTriggering: boolean) => void;
  hasChatStarted: boolean;
  setHasChatStarted: (started: boolean) => void;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

const initialState: ReportState = {
  overview: null,
  specificationAnalysis: [],
  report: null,
  threadId: null,
  analysisHistory: [],
};

export const ReportProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [reportState, setReportState] = useState<ReportState>(initialState);
  const [isResetting, setIsResetting] = useState(false);
  const [isAutoTriggeringFromUrl, setIsAutoTriggeringFromUrl] = useState(false);
  const [hasChatStarted, setHasChatStarted] = useState(false);

  const updateOverview = (overview: string) => {
    // Don't update if we're in the middle of resetting
    if (isResetting) return;
    setReportState((prev) => ({ ...prev, overview }));
  };

  const updateSpecificationAnalysis = (analysis: SpecificationAnalysis[]) => {
    // Don't update if we're in the middle of resetting
    if (isResetting) return;
    setReportState((prev) => ({ ...prev, specificationAnalysis: analysis }));
  };

  const updateReport = (report: ReportData) => {
    // Don't update if we're in the middle of resetting
    if (isResetting) return;
    setReportState((prev) => ({ ...prev, report }));
  };

  const updateThreadId = (threadId: string) => {
    // Don't update if we're in the middle of resetting
    if (isResetting) return;
    setReportState((prev) => ({ ...prev, threadId }));
  };

  const updateAnalysisHistory = (history: AnalysisHistoryItem[]) => {
    // Don't update if we're in the middle of resetting
    if (isResetting) return;
    setReportState((prev) => ({ ...prev, analysisHistory: history }));
  };

  const clearAll = () => {
    setReportState(initialState);
  };

  const setResetting = (resetting: boolean) => {
    setIsResetting(resetting);
  };

  return (
    <ReportContext.Provider
      value={{
        reportState,
        updateOverview,
        updateSpecificationAnalysis,
        updateReport,
        updateThreadId,
        updateAnalysisHistory,
        clearAll,
        isResetting,
        setResetting,
        isAutoTriggeringFromUrl,
        setIsAutoTriggeringFromUrl,
        hasChatStarted,
        setHasChatStarted,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};

export const useReport = () => {
  const context = useContext(ReportContext);
  if (context === undefined) {
    throw new Error("useReport must be used within a ReportProvider");
  }
  return context;
};